/**
 * Karcis (bahelastruk) layout geometry — single source of truth.
 *
 * Two structural rules replace every hand-tuned offset the page used to carry:
 *
 * 1. ART IS DATA. Each retro-window asset has its intrinsic pixel size and its
 *    interior content "window" (the region inside the drawn chrome where live
 *    content belongs), measured pixel-exactly from the shipped .webp files and
 *    expressed as fractions of the art box. Overlays position off these
 *    fractions, so art ↔ content alignment holds at any rendered size — there
 *    is nothing left to eyeball.
 *
 * 2. FRAMES FIT THEIR STAGE. Every frame is capped both by a share of the
 *    canvas width AND a share of the canvas height, and keeps its intrinsic
 *    aspect ratio. Because the page renders on a fixed design canvas (the
 *    project's screenSize inside View's ScaleToFit shell), a height cap is
 *    expressible as a width percentage once the canvas aspect is known:
 *    width = min(maxW·W, maxH·H·ratio)  →  min(maxW%, (maxH·H/W·ratio)%).
 *    The page computes H/W at render (portrait rotation flips it), so no
 *    canvas size or monitor can push a frame out of its stage again.
 *
 * The QA harness (`ui-sim/karcis`) consumes this exact module — keep every
 * value here, never inline in the page.
 */

import type { CSSProperties } from 'react'

export interface ArtWindow {
  /** Fractions of the art box (0..1). */
  x: number
  y: number
  w: number
  h: number
}

export interface ArtSpec {
  /** Intrinsic pixel size of the shipped asset (true aspect — never distort it). */
  w: number
  h: number
  /** Interior content region, measured from the art. Absent = plain image. */
  window?: ArtWindow
}

export interface FrameSize {
  /** Max width as a fraction of the canvas width. */
  maxW: number
  /** Max height as a fraction of the canvas height. */
  maxH: number
}

/** Measured from src/renderer/public/assets/bahela-karcis (canvas pixel scan). */
export const KARCIS_ART = {
  karcisBig: { w: 862, h: 275 },
  mulaiSesi: { w: 364, h: 79 },
  titleSmall: { w: 759, h: 56 },
  pratinjau: { w: 371, h: 346, window: { x: 0.0216, y: 0.1908, w: 0.9407, h: 0.7688 } },
  bayar: { w: 448, h: 418, window: { x: 0.0179, y: 0.1579, w: 0.9509, h: 0.8086 } },
  pemutarFoto: { w: 794, h: 739, window: { x: 0.0252, y: 0.0812, w: 0.9408, h: 0.8809 } },
  /** `window` here is the free band BELOW the printed "Tunggu Sebentar" art — the progress bar's home. */
  diproses: { w: 691, h: 300, window: { x: 0.06, y: 0.6, w: 0.88, h: 0.28 } },
  /** Gray viewer area (inside the scrollbar chrome). It is exactly square — a 1:1 photo fills it. */
  pemutarMedia: { w: 505, h: 574, window: { x: 0.0396, y: 0.1516, w: 0.8733, h: 0.7683 } },
  silahkan: { w: 430, h: 38 }
} as const satisfies Record<string, ArtSpec>

export type KarcisArtKey = keyof typeof KARCIS_ART

/**
 * Stage budget per frame. maxW keeps side-by-side compositions from colliding;
 * maxH keeps every frame (title + margins included) inside its 1-canvas stage.
 * On a 1920×1080 canvas these resolve close to the original design's sizes,
 * minus the two frames that historically overflowed (bayar, pemutar-foto).
 */
export const KARCIS_SIZE = {
  karcisBig: { maxW: 0.625, maxH: 0.42 },
  mulaiSesi: { maxW: 0.26, maxH: 0.1 },
  titleSmall: { maxW: 0.52, maxH: 0.065 },
  pratinjau: { maxW: 0.3, maxH: 0.62 },
  bayar: { maxW: 0.44, maxH: 0.72 },
  pemutarFoto: { maxW: 0.6, maxH: 0.88 },
  diproses: { maxW: 0.52, maxH: 0.4 },
  pemutarMedia: { maxW: 0.34, maxH: 0.74 },
  silahkan: { maxW: 0.31, maxH: 0.05 }
} as const satisfies Record<KarcisArtKey, FrameSize>

export type KarcisStage = 1 | 2 | 3 | 4 | 5

/**
 * Where each stage lives on the 5-screen strip, as a % of the strip height.
 * ONE value per stage, used for BOTH the stage panel's `top` and the strip's
 * translateY when that stage is active — the panel IS the visible window, so
 * content centered in the panel is centered on screen.
 *
 * The legacy page kept two tables (panel position vs scroll position) that
 * disagreed by 2% of the strip (= 10% of a screen) for every stage except
 * payment, which parked all "centered" content ~108 canvas px below the
 * screen's center and clipped the bottom of the tallest frame.
 */
export const KARCIS_STAGE_POSITIONS: Record<KarcisStage, number> = {
  1: 0,
  2: 30,
  3: 64,
  4: 70,
  5: 76
}

export const artRatio = (key: KarcisArtKey): number => KARCIS_ART[key].w / KARCIS_ART[key].h

/**
 * CSS width for a frame: both caps folded into one `min()` of width
 * percentages. `hOverW` = canvasHeight / canvasWidth for the ACTIVE
 * orientation — the only runtime input.
 */
export const frameWidthCss = (key: KarcisArtKey, hOverW: number): string => {
  const { maxW, maxH } = KARCIS_SIZE[key]
  const byHeight = maxH * hOverW * artRatio(key) * 100
  return `min(${(maxW * 100).toFixed(2)}%, ${byHeight.toFixed(2)}%)`
}

/** Absolute-position style for an art's content window, relative to the frame box. */
export const windowStyle = (key: KarcisArtKey): CSSProperties => {
  const win = (KARCIS_ART[key] as ArtSpec).window
  if (!win) return {}
  return {
    left: `${(win.x * 100).toFixed(2)}%`,
    top: `${(win.y * 100).toFixed(2)}%`,
    width: `${(win.w * 100).toFixed(2)}%`,
    height: `${(win.h * 100).toFixed(2)}%`
  }
}

/** Intrinsic ratio as a CSS aspect-ratio value — reserves space before the asset loads. */
export const artAspectCss = (key: KarcisArtKey): string =>
  `${KARCIS_ART[key].w} / ${KARCIS_ART[key].h}`
